import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/free-mode';
import { CaseItem } from '@/interfaces';
import { CaseCard } from 'components/common';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
	casesData: CaseItem[];
}

// Interface para las props del componente CustomPagination
interface CustomPaginationProps {
	swiper: SwiperType | null;
	slidesCount: number;
	currentSlide: number;
}

// Componente de paginación personalizado para móvil
const CustomPagination: React.FC<CustomPaginationProps> = ({
	swiper,
	slidesCount,
	currentSlide,
}) => {
	// Calcular qué dots mostrar (máximo 3)
	const totalDots = slidesCount;
	let dotsToShow: number[] = [];

	if (totalDots <= 3) {
		// Mostrar todos si hay 3 o menos
		dotsToShow = Array.from({ length: totalDots }, (_, i) => i);
	} else {
		// Mostrar máximo 3 dots centrados alrededor del activo
		if (currentSlide === 0) {
			dotsToShow = [0, 1, 2];
		} else if (currentSlide === totalDots - 1) {
			dotsToShow = [totalDots - 3, totalDots - 2, totalDots - 1];
		} else {
			dotsToShow = [currentSlide - 1, currentSlide, currentSlide + 1];
		}
	}

	const goToSlide = (index: number) => {
		if (swiper) {
			swiper.slideTo(index);
		}
	};

	return (
		<div className="flex justify-center items-center space-x-2 md:hidden">
			{dotsToShow.map((index) => (
				<button
					key={index}
					onClick={() => goToSlide(index)}
					className={`w-3 h-3 rounded-full transition-all duration-300 ${
						index === currentSlide ? 'bg-purple-600 scale-125' : 'bg-gray-300'
					}`}
					aria-label={`Ir a slide ${index + 1}`}
				/>
			))}
		</div>
	);
};

export const Carousel: React.FC<Props> = ({ casesData }) => {
	const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const [isMobile, setIsMobile] = useState(false);

	// Detectar si estamos en móvil
	useEffect(() => {
		const checkIsMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};

		// Verificar al montar el componente
		checkIsMobile();

		// Escuchar cambios de tamaño de ventana
		window.addEventListener('resize', checkIsMobile);

		// Limpiar el event listener al desmontar
		return () => {
			window.removeEventListener('resize', checkIsMobile);
		};
	}, []);

	return (
		<div
			className="w-full mt-7 md:mt-[10vh] 3xl:pl-[170px]"
			style={{ contentVisibility: 'auto', containIntrinsicSize: '1px 900px' }}
		>
			<Swiper
				spaceBetween={20}
				slidesPerView={1}
				modules={[Pagination, Navigation]}
				grabCursor={true}
				onSwiper={setSwiperInstance}
				onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
				breakpoints={{
					375: {
						slidesPerView: 1,
						spaceBetween: 50,
					},
					425: {
						slidesPerView: 1,
						spaceBetween: 50,
						slidesOffsetAfter: 45,
						slidesOffsetBefore: 0,
					},
					640: {
						slidesPerView: 2,
						spaceBetween: 24,
						slidesOffsetAfter: 0,
						slidesOffsetBefore: 45,
					},
					768: {
						slidesPerView: 3,
						spaceBetween: 250,
						slidesOffsetAfter: 250,
						slidesOffsetBefore: 45,
					},
					1024: {
						slidesPerView: 3,
						spaceBetween: 10,
						slidesOffsetAfter: 70,
						slidesOffsetBefore: 85,
					},
					1280: {
						slidesPerView: 4,
						spaceBetween: 90,
						slidesOffsetAfter: 160,
						slidesOffsetBefore: 85,
					},
					1336: {
						slidesPerView: 4,
						spaceBetween: 24,
						slidesOffsetAfter: 80,
						slidesOffsetBefore: 85,
					},
					1440: {
						slidesPerView: 5,
						spaceBetween: 270,
						slidesOffsetAfter: 320,
						slidesOffsetBefore: 85,
					},
					1536: {
						slidesPerView: 5,
						spaceBetween: 200,
						slidesOffsetAfter: 240,
						slidesOffsetBefore: 85,
					},
					1560: {
						slidesPerView: 5,
						spaceBetween: 150,
						slidesOffsetAfter: 220,
						slidesOffsetBefore: 85,
					},
					1700: {
						slidesPerView: 5,
						spaceBetween: 200,
						slidesOffsetBefore: 80,
						slidesOffsetAfter: 200,
					},
					1792: {
						slidesPerView: 5,
						spaceBetween: 100,
						slidesOffsetBefore: 80,
						slidesOffsetAfter: 100,
					},
					1900: {
						slidesPerView: 6,
						spaceBetween: 150,
						slidesOffsetBefore: 80,
						slidesOffsetAfter: 140,
					},
					1920: {
						slidesPerView: 5,
						spaceBetween: 10,
						slidesOffsetBefore: 80,
						slidesOffsetAfter: 210,
					},
				}}
				// Configuración condicional de paginación
				pagination={
					!isMobile
						? {
								el: '.custom-pagination-native',
								clickable: true,
								renderBullet: (_index, className) => {
									return `<button class="${className} my-custom-dot-class"></button>`;
								},
						  }
						: false
				}
				// navigation={{
				// 	nextEl: '.custom-next-cases',
				// 	prevEl: '.custom-prev-cases',
				// }}
				className={clsx('relative w-full')}
			>
				{casesData.map((value, index) => (
					<SwiperSlide key={index} className="pb-2">
						<div className="w-full min-w-[330px] max-w-[330px] mx-auto">
							<CaseCard
								className="md:h-[404px] w-full h-[368px] py-8 !px-6"
								{...value}
							/>
						</div>
						{/* <CaseCard
							className="md:h-[404px] md:w-[330px] w-[346px] h-[368px] py-8 !px-6"
							{...value}
						/> */}
					</SwiperSlide>
				))}
			</Swiper>

			<div className="flex w-full items-center justify-center gap-x-10 mt-10 lg:mt-[75px] px-[45px] lg:px-[85px]">
				<button
					className="custom-prev-cases min-w-12 min-h-12 !w-12 !h-12 border border-purple-150 rounded-full flex justify-center items-center"
					onClick={() => swiperInstance?.slidePrev()}
					aria-label="Previous Slide"
				>
					<ChevronLeft
						width={42}
						height={42}
						className="text-primary stroke-1"
					/>
				</button>

				{/* Paginación nativa para escritorio */}
				{!isMobile && (
					<div className="custom-pagination-native !w-fit items-center md:flex justify-center gap-x-[10px] min-h-[20px]" />
				)}

				{/* Paginación personalizada para móvil */}
				{isMobile && (
					<CustomPagination
						swiper={swiperInstance}
						slidesCount={casesData.length}
						currentSlide={currentSlide}
					/>
				)}

				<button
					className="custom-next-cases min-w-12 min-h-12 !w-12 !h-12 border border-purple-150 rounded-full flex justify-center items-center"
					onClick={() => swiperInstance?.slideNext()}
					aria-label="Next Slide"
				>
					<ChevronRight
						width={42}
						height={42}
						className="text-primary stroke-1"
					/>
				</button>
			</div>
		</div>
	);
};
